import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import { BlockUI } from 'primereact/blockui';
import PageHeader, { EmptyState } from '../common/PageHeader';
import { akcizeApi } from '../../api/services';
import { exportXlsx, stampaVremena } from '../../utils/excel';
import TabelaVozila from './TabelaVozila';
import Kontrole from './Kontrole';
import Nabavke from './Nabavke';
import Uvoz from './Uvoz';
import { mesecNaziv } from './format';

const KVARTALI = [{ label: 'I', value: 1 }, { label: 'II', value: 2 }, { label: 'III', value: 3 }, { label: 'IV', value: 4 }];
const danas = new Date();
const GODINE = Array.from({ length: 10 }, (_, i) => danas.getFullYear() - i).map((g) => ({ label: String(g), value: g }));
/* podrazumevano: prethodni završeni kvartal (zahtev se podnosi posle isteka kvartala) */
const pretKv = (() => { const k = Math.floor(danas.getMonth() / 3); return k === 0 ? { g: danas.getFullYear() - 1, k: 4 } : { g: danas.getFullYear(), k }; })();

/* izvor km u izvozu: potvrđeno / PROCENA (operater mora da je proveri pre podnošenja) */
const izvorKm = (c) => (c.km == null ? '' : `${c.potvrdjen ? '' : 'PROCENA — '}${c.izvorOpis}`);

/* Akcize — podloga za zahtev za refakciju akcize na gorivo (REF-T):
   kilometri iz tahografa (očitavanja vozila), za analogne tahografe procena koju operater potvrđuje,
   gorivo izdato vozilima, nabavke; kontrole pre podnošenja. */
export default function Akcize() {
    const companies = useSelector((s) => s.companies.list);
    const firme = useMemo(() => (companies || []).filter((c) => c.id != null).map((c) => ({ label: c.name, value: c.id })), [companies]);
    const [companyId, setCompanyId] = useState(null);
    const [godina, setGodina] = useState(pretKv.g);
    const [kvartal, setKvartal] = useState(pretKv.k);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState(0);

    const ucitaj = async (id = companyId) => {
        if (!id) return;
        setBusy(true);
        try { setData(await akcizeApi.tabela({ companyId: id, godina, kvartal })); } catch { /* toast */ }
        setBusy(false);
    };

    const brGresaka = data ? data.kontrole.filter((k) => k.nivo === 'greska').length : 0;
    const brUpoz = data ? data.kontrole.filter((k) => k.nivo === 'upozorenje').length : 0;
    const firma = firme.find((f) => f.value === companyId);

    const izvoz = () => {
        const kolone = [{ header: 'Reg. oznaka', key: 'reg' }];
        data.period.meseci.forEach((m) => {
            kolone.push({ header: `${mesecNaziv(m, true)} km`, value: (r) => r.meseci[m].km, numFmt: '#,##0' });
            kolone.push({ header: `${mesecNaziv(m, true)} izvor km`, value: (r) => izvorKm(r.meseci[m]), width: 22 });
            kolone.push({ header: `${mesecNaziv(m, true)} L`, value: (r) => r.meseci[m].litara, numFmt: '#,##0.00' });
            kolone.push({ header: `${mesecNaziv(m, true)} L/100`, value: (r) => r.meseci[m].potrosnja, numFmt: '0.00' });
        });
        kolone.push({ header: 'Kvartal km', value: (r) => r.ukupno.km, numFmt: '#,##0' }, { header: 'Km potvrđeni', value: (r) => (r.ukupno.procena ? 'NE — ima procena' : 'da') }, { header: 'Kvartal L', value: (r) => r.ukupno.litara, numFmt: '#,##0.00' },
            { header: 'Kvartal L/100', value: (r) => r.ukupno.potrosnja, numFmt: '0.00' }, { header: 'Prosek pret. god. L/100', value: (r) => r.prosekPrethodne, numFmt: '0.00' },
            { header: 'Granica +5% (L)', value: (r) => r.dozvoljeno, numFmt: '#,##0.00' });
        exportXlsx({
            fileName: `Akcize_${(firma ? firma.label : '').replace(/[^\w-]+/g, '_')}_${godina}_Q${kvartal}.xlsx`,
            sheets: [
                { sheetName: `Vozila Q${kvartal} ${godina}`, title: `${firma ? firma.label : ''} — tabela vozila, ${kvartal}. kvartal ${godina}.`, subtitle: `Kilometri iz tahografa; bez DDD-a (analogni tahograf) procena označena u koloni „izvor km“ (Tacho Royal Team) · ${data.procena ? `NEPOTVRĐENIH PROCENA: ${data.procena} · ` : ""}${stampaVremena()}`, columns: kolone, rows: data.redovi },
                { sheetName: 'Nabavke', title: 'Nabavke goriva', columns: [
                    { header: 'Datum', value: (n) => String(n.datum).slice(0, 10) }, { header: 'Broj računa', key: 'brojRacuna' }, { header: 'Količina (L)', key: 'kolicina', numFmt: '#,##0.00' },
                    { header: 'Dobavljač', key: 'dobavljac' }, { header: 'Iznos (din)', key: 'iznos', numFmt: '#,##0.00' }, { header: 'Broj izvoda', key: 'brojIzvoda' }], rows: data.nabavke },
                { sheetName: 'Kontrola', title: 'Kontrola pre podnošenja', columns: [{ header: 'Nivo', key: 'nivo' }, { header: 'Opis', key: 'poruka', wrap: true, width: 110 }], rows: data.kontrole },
            ],
        });
    };

    return (
        <div className="page akcize">
            <PageHeader title="Akcize — refakcija goriva" subtitle="Podloga za zahtev za povraćaj akcize (REF-T): kilometri iz tahografa (za analogne tahografe procena koju potvrđujete), gorivo po vozilu, nabavke i kontrola pre podnošenja.">
                {data && <Button label="Izvoz u Excel" icon="pi pi-file-excel" outlined onClick={izvoz} disabled={!data.redovi.length && !data.nabavke.length} />}
            </PageHeader>

            <section className="panel">
                <div className="filters">
                    <div className="field grow">
                        <label htmlFor="akc-firma">Kompanija <span className="req">*</span></label>
                        <Dropdown inputId="akc-firma" value={companyId} options={firme} onChange={(e) => { setCompanyId(e.value); setData(null); ucitaj(e.value); }}
                            placeholder="Izaberite kompaniju" filter />
                    </div>
                    <div className="field">
                        <label htmlFor="akc-god">Godina</label>
                        <Dropdown inputId="akc-god" value={godina} options={GODINE} onChange={(e) => setGodina(e.value)} />
                    </div>
                    <div className="field">
                        <label id="akc-kv-l">Kvartal</label>
                        <SelectButton className="akc-kvartal" value={kvartal} options={KVARTALI} onChange={(e) => e.value && setKvartal(e.value)} aria-labelledby="akc-kv-l" />
                    </div>
                    <div className="filters-actions">
                        <Button label="Prikaži" icon="pi pi-search" onClick={() => ucitaj()} disabled={!companyId} loading={busy} />
                    </div>
                </div>
            </section>

            {!companyId ? (
                <EmptyState icon="pi pi-building" title="Izaberite kompaniju" text="Tabela, nabavke i kontrola se prikazuju po kompaniji i kvartalu." />
            ) : (
                <BlockUI blocked={busy}>
                    <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
                        <TabPanel header="Tabela vozila" leftIcon="pi pi-truck">
                            {data && <TabelaVozila data={data} companyId={companyId} onIzmena={() => ucitaj()} />}
                        </TabPanel>
                        <TabPanel header={`Kontrola${data ? ` (${brGresaka} gr. · ${brUpoz} upoz.)` : ''}`} leftIcon="pi pi-shield">
                            {data && <Kontrole kontrole={data.kontrole} />}
                        </TabPanel>
                        <TabPanel header="Nabavke" leftIcon="pi pi-receipt">
                            {data && <Nabavke data={data} companyId={companyId} onIzmena={() => ucitaj()} />}
                        </TabPanel>
                        <TabPanel header="Uvoz iz Excela" leftIcon="pi pi-upload">
                            <Uvoz companyId={companyId} onUvezeno={() => ucitaj()} />
                        </TabPanel>
                    </TabView>
                </BlockUI>
            )}
        </div>
    );
}
